import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ViewStyle, useWindowDimensions } from 'react-native';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebHeader } from './WebHeader';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { useEntranceCascade } from '../motion/Motion';

interface WebPageShellProps {
  children: React.ReactNode;
  title?: string;
  scroll?: boolean;
  contentStyle?: ViewStyle;
  maxWidth?: number;
}

// Centers native-app screens in a fixed-width column for desktop browsers —
// the mobile screens are edge-to-edge by design, which reads as broken on wide viewports.
export function WebPageShell({ children, title, scroll = true, contentStyle, maxWidth }: WebPageShellProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const navigation = useNavigation<any>();
  const route = useRoute<any>();

  // Only set when this screen was reached via a dashboard quick-link (see
  // HomeScreen.web.tsx), which explicitly passes backTo: 'Home' - never true
  // for a screen reached directly from the sidebar (WebSidebar explicitly
  // clears it on every click), so the button never appears as a dead click.
  const backTo = route.params?.backTo;

  // Every page's sections rise into place in turn - when it opens, and again when it is
  // come back to - and content that loads later rises in too (components/motion).
  const cascadeRef = useEntranceCascade(useIsFocused());

  const content = (
    <View style={[styles.column, maxWidth ? { maxWidth } : undefined, contentStyle]}>
      {!!backTo && (
        <TouchableOpacity
          style={styles.backRow}
          onPress={() => navigation.goBack()}
          accessibilityLabel={`Back to ${backTo}`}
        >
          <FeatherIcon name="arrow-left" size={16} color={colors.primary} />
          <Text style={styles.backText}>Back to {backTo}</Text>
        </TouchableOpacity>
      )}
      <View ref={cascadeRef} style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        {children}
      </View>
    </View>
  );

  return (
    <View style={styles.page}>
      {title && <WebHeader title={title} />}
      {scroll ? (
        <ScrollView showsVerticalScrollIndicator={true} contentContainerStyle={[styles.scrollContent, isMobile && styles.scrollContentMobile]}>
          {content}
        </ScrollView>
      ) : (
        <View style={[styles.scrollContent, isMobile && styles.scrollContentMobile]}>{content}</View>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  page: { flex: 1, backgroundColor: colors.background },
  scrollContent: { alignItems: 'center', paddingVertical: 32, paddingHorizontal: 40, flexGrow: 1 },
  scrollContentMobile: { paddingVertical: 16, paddingHorizontal: 16 },
  column: { width: '100%', maxWidth: 1000 },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 16,
    paddingVertical: 4,
  },
  backText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: colors.primary,
  },
  cardContainer: {
    backgroundColor: '#EEF3FA',
    borderRadius: 24,
    padding: 32,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 20,
    elevation: 2,
    borderWidth: 1,
    borderColor: colors.divider,
  },
  cardContainerMobile: {
    padding: 10,
    borderRadius: 16,
  },
});
